/**
 * Content API — guest content, paywall content, suggested prompts, etc.
 * The /content endpoints serve language-resolved, ETag-cached blobs of UI
 * copy and per-context configuration.
 */

import type { AxiosInstance } from 'axios';
import type { GuestContent, GuestQuotaContent } from '@wg/types/content';

const CONTENT_BASE = '/functions/v1/content';

export interface ContentApi {
  fetchGuestContent(language?: string): Promise<GuestContent>;
  fetchGuestQuotaExhausted(
    sessionId: string,
    options?: {
      language?: string;
      abVariant?: string;
      quotaUsed?: number;
      quotaLimit?: number;
      timeOfDay?: 'morning' | 'afternoon' | 'evening' | 'night';
      latitude?: number;
      longitude?: number;
    },
  ): Promise<GuestQuotaContent>;
}

export function createContentApi(http: AxiosInstance): ContentApi {
  return {
    async fetchGuestContent(language: string = 'en-US'): Promise<GuestContent> {
      const res = await http.get<GuestContent>(`${CONTENT_BASE}/guest`, {
        params: { language },
      });
      return res.data;
    },
    async fetchGuestQuotaExhausted(
      sessionId,
      options,
    ): Promise<GuestQuotaContent> {
      const params: Record<string, string> = {
        language: options?.language || 'en-US',
        ab_variant: options?.abVariant || 'A',
      };
      if (options?.quotaUsed !== undefined)
        params.quota_used = String(options.quotaUsed);
      if (options?.quotaLimit !== undefined)
        params.quota_limit = String(options.quotaLimit);
      if (options?.timeOfDay) params.time_of_day = options.timeOfDay;
      if (options?.latitude !== undefined)
        params.latitude = String(options.latitude);
      if (options?.longitude !== undefined)
        params.longitude = String(options.longitude);
      const res = await http.get<GuestQuotaContent>(
        `${CONTENT_BASE}/sessions/${sessionId}/guest-quota-exhausted`,
        { params },
      );
      return res.data;
    },
  };
}

export type { GuestContent, GuestQuotaContent } from '@wg/types/content';
